import { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import { useDispatch, useSelector } from "react-redux";
import _ from "@lodash";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import Box from "@mui/system/Box";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import FormControl from "@mui/material/FormControl";
import { Select, MenuItem, InputLabel, Typography, Divider, Grid, Switch, FormControlLabel } from "@mui/material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDeepCompareEffect } from "@fuse/hooks";
import DeleteIcon from "@mui/icons-material/Delete";

import { getEmpAdmins, selectEmpAdmin } from "../../empAdmin/store/empAdminSlice";
import { addLocalAdmin } from "../store/localAdminSlice";
import { ApiUserAdmin, endpoints_localAdmin } from "../../../api";
import { getUsers, selectUser } from "../../userAdmin/store/userSlice";

const api = new ApiUserAdmin();

const plans = [
  { name: "Basic", id: 1 },
  { name: "Premium", id: 2 },
];

function LocalForm({ handleClose }) {
  const [premiumChecked, setPremiumChecked] = useState(false);
  const dispatch = useDispatch();
  const empAdmins = useSelector(selectEmpAdmin);
  const users = useSelector(selectUser);

  useDeepCompareEffect(() => {
    dispatch(getEmpAdmins());
    dispatch(getUsers());
  }, [dispatch]);

  const schema = yup.object().shape({
    fantasy_name: yup.string().trim().required("Nombre de fantasia es requerido"),
    premium: yup.boolean(),
    address: yup.string().required("Sucursal es requerido"),
    company_name: yup.string().trim().required("Nombre de compañia es requerido"),
    id_emp_admin: yup.string().optional(),
    rut: yup.string().trim().required("Rut es requerido"),
    num_auth_sani: yup.string().nullable().optional(),
    client_id: yup.string().nullable().optional(),
    auditor_id: yup.string().nullable().optional(),
    supervisor_id: yup.string().nullable().optional(),
  });

  const { control, reset, handleSubmit, formState } = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
    defaultValues: {
      fantasy_name: "",
      premium: false,
      address: "",
      company_name: "",
      id_emp_admin: "",
      rut: "",
      num_auth_sani: "",
      client_id: "",
      auditor_id: "",
      supervisor_id: "",
    },
  });

  const { isValid, dirtyFields, errors } = formState;

  const handlePremiumChange = () => setPremiumChecked(!premiumChecked);

  async function onSubmit(data) {
    try {
      data.fantasy_name = data.fantasy_name.trim().toUpperCase();
      const duplicado = await api.post(endpoints_localAdmin.LOCAL_NOM, data);
      if (duplicado.length > 0) {
        toast.error("La empresa ya existe!", { position: "top-center", autoClose: 2000 });
      } else {
        data.premium = premiumChecked;
        dispatch(addLocalAdmin(data)).then(() => {
          toast.success("Local creado con exito", { position: "top-center", autoClose: 1000 });
          handleClose();
        });
      }
    } catch (err) {
      toast.error("No se pudo crear el local", { position: "top-center", autoClose: 1000 });
    }
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, overflowY: "auto" }}>
      <Typography variant="h5" fontWeight="bold" textAlign="center" mb={2}>
        Nuevo Local
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          {/* Nombre de Fantasia */}
          <Grid item xs={12} sm={6}>
            <Controller
              control={control}
              name="fantasy_name"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Nombre de fantasia"
                  placeholder="Nombre de fantasia"
                  fullWidth
                  required
                  error={!!errors.fantasy_name}
                  helperText={errors?.fantasy_name?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FuseSvgIcon size={20}>heroicons-solid:user-circle</FuseSvgIcon>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {/* Sucursal */}
          <Grid item xs={12} sm={6}>
            <Controller
              control={control}
              name="address"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Sucursal"
                  placeholder="Lugar Sucursal"
                  fullWidth
                  required
                  error={!!errors.address}
                  helperText={errors?.address?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FuseSvgIcon size={20}>heroicons-solid:map</FuseSvgIcon>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {/* Compañia */}
          <Grid item xs={12} sm={6}>
            <Controller
              control={control}
              name="company_name"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Nombre Compañia"
                  fullWidth
                  required
                  error={!!errors.company_name}
                  helperText={errors?.company_name?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FuseSvgIcon size={20}>heroicons-outline:identification</FuseSvgIcon>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {/* Rut */}
          <Grid item xs={12} sm={6}>
            <Controller
              control={control}
              name="rut"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Rut"
                  fullWidth
                  required
                  error={!!errors.rut}
                  helperText={errors?.rut?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FuseSvgIcon size={20}>heroicons-solid:identification</FuseSvgIcon>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {/* Cliente Administrador */}
          <Grid item xs={12} sm={6}>
            <Controller
              control={control}
              name="client_id"
              render={({ field: { onChange, value } }) => (
                <FormControl fullWidth>
                  <InputLabel>Cliente Administrador</InputLabel>
                  <Select value={value || ""} onChange={onChange}>
                    {users
                      .filter(u => u.rol_id === 4)
                      .map(u => (
                        <MenuItem key={u.id} value={u.id}>
                          {u.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>

          {/* Supervisor */}
          <Grid item xs={12} sm={6}>
            <Controller
              control={control}
              name="supervisor_id"
              render={({ field: { onChange, value } }) => (
                <FormControl fullWidth>
                  <InputLabel>Supervisor</InputLabel>
                  <Select value={value || ""} onChange={onChange}>
                    {users
                      .filter(u => u.rol_id === 2)
                      .map(u => (
                        <MenuItem key={u.id} value={u.id}>
                          {u.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>

          {/* Auditor */}
          {/* <Grid item xs={12} sm={6}>
            <Controller
              control={control}
              name="auditor_id"
              render={({ field: { onChange, value } }) => (
                <FormControl fullWidth>
                  <InputLabel>Auditor</InputLabel>
                  <Select value={value || ""} onChange={onChange}>
                    {users
                      .filter(u => u.rol_id === 3)
                      .map(u => (
                        <MenuItem key={u.id} value={u.id}>
                          {u.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid> */}

          {/* Empresa Administradora */}
          <Grid item xs={12} sm={6}>
            <Controller
              control={control}
              name="id_emp_admin"
              render={({ field: { onChange, value } }) => (
                <FormControl fullWidth>
                  <InputLabel>Empresa Administradora</InputLabel>
                  <Select value={value || ""} onChange={onChange}>
                    {empAdmins.map(emp => (
                      <MenuItem key={emp.id} value={emp.id}>
                        {emp.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>

          {/* Numero autorización sanitaria */}
          <Grid item xs={12} sm={6}>
            <Controller
              control={control}
              name="num_auth_sani"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Número autorización sanitaria"
                  placeholder="1111 12-12-2024"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FuseSvgIcon size={20}>heroicons-solid:document</FuseSvgIcon>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {/* Premium */}
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={premiumChecked} onChange={handlePremiumChange} />}
              label="Premium"
            />
          </Grid>
        </Grid>

        {/* Botones */}
        <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="outlined" color="inherit" onClick={handleClose}>
            Cancelar
          </Button>
          <Button variant="contained" color="secondary" type="submit">
            Guardar
          </Button>
          <Button variant="outlined" color="error" startIcon={<DeleteIcon />}>
            Eliminar
          </Button>
        </Box>
      </form>
    </Box>
  );
}

export default LocalForm;
